// AuthCallback — v0.7.0 (SSO / Social Login)
//
// Landing page for the OAuth round-trip. When a provider (Google, and
// later Apple / Microsoft) redirects the browser back here, the Supabase
// client's detectSessionInUrl finishes establishing the session, which
// flows into AuthContext. This page just waits for that to resolve, then
// forwards the user to wherever they were originally headed.
//
// The intended destination is read from sessionStorage — Login.js stashes
// it there before kicking off OAuth, because the provider round-trip wipes
// React Router's in-memory location state.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isOnboardingAllowed } from '../lib/onboardingAllowlist';
import { trackFunnel, getPlanIntent, clearPlanIntent, FUNNEL_EVENTS } from '../lib/attribution';
import { observeOnboarding } from '../lib/observeOnboarding';

// Must match the key Login.js writes to.
const POST_AUTH_REDIRECT_KEY = 'rv-str-postauth-redirect';

const pageStyle = {
  background: '#f3f4f6',
  minHeight: '100vh',
  padding: '60px 20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
};

const cardStyle = {
  background: '#ffffff',
  borderRadius: '16px',
  padding: '36px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  width: '100%',
  maxWidth: '440px',
  textAlign: 'center',
};

// OAuth providers report failures in either the query string or the hash
// fragment, depending on the flow. Check both.
function readUrlError() {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(
    window.location.hash.startsWith('#') ? window.location.hash.slice(1) : ''
  );
  const code = search.get('error') || hash.get('error');
  if (!code) return '';
  const description =
    search.get('error_description') || hash.get('error_description');
  return description ? description.replace(/\+/g, ' ') : code;
}

export default function AuthCallback() {
  const { user, loading, isConfigured, signOut } = useAuth();
  const navigate = useNavigate();

  // Read the provider error (if any) once, on mount.
  const [urlError] = useState(readUrlError);

  // Controlled Onboarding cohort gate. An OAuth round-trip can resolve a
  // session for anyone with a provider account; for the invite-only cohort we
  // admit only allowlisted (or admin) emails. A non-cohort user is signed out
  // here and shown an invite-only notice — they never reach a protected route.
  const [denied, setDenied] = useState(false);

  // Resolve — and clear — the stashed destination exactly once.
  const destination = useMemo(() => {
    let dest = '/dashboard';
    try {
      const stashed = window.sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
      if (stashed) dest = stashed;
      window.sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
    } catch {
      // sessionStorage unavailable — fall back to the default destination.
    }
    return dest;
  }, []);

  // Once the session settles, enforce the cohort gate, then forward onward.
  useEffect(() => {
    if (loading) return;
    if (!user) return;
    if (isOnboardingAllowed(user.email)) {
      // BL-038 Story 38.4 — activation is the end of the conversion funnel.
      // Record it with the carried plan intent (a non-billing demand signal),
      // then clear the intent. This does NOT change the user's tier — tier stays
      // an admin action; activation only starts the standard trial (BL-040/041).
      trackFunnel(FUNNEL_EVENTS.ACTIVATION_COMPLETE, { plan_intent: getPlanIntent() || null });
      clearPlanIntent();
      // PLATFORM-006 (ADR-064) — fire-and-forget server-authoritative onboarding
      // observation (participant.account_created + first_login_completed). NEVER
      // awaited: it must not delay navigation. Idempotent + retried next login.
      observeOnboarding();
      navigate(destination, { replace: true });
      return;
    }
    // Not on the invite list — end the session and show the invite-only state.
    setDenied(true);
    if (typeof signOut === 'function') {
      Promise.resolve(signOut()).catch(() => {});
    }
  }, [loading, user, destination, navigate, signOut]);

  // Cohort gate: a valid provider session for an email that isn't invited.
  if (denied) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#111827', margin: '0 0 10px' }}>
            You're not on the invite list yet
          </h1>
          <p style={{ color: '#6b7280', fontSize: '15px', lineHeight: 1.5, margin: '0 0 22px' }}>
            Rentava is currently in invite-only controlled onboarding. Your account isn't on the
            cohort list, so we've signed you back out. If you believe this is a mistake, reply to
            your invitation or contact the Rentava team.
          </p>
          <Link
            to="/login"
            style={{
              display: 'inline-block',
              background: '#1d4ed8',
              color: '#ffffff',
              textDecoration: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontWeight: 700,
              fontSize: '15px',
            }}
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  // Failure: the provider returned an error, auth isn't configured, or the
  // session never resolved after loading finished.
  const failed = urlError || !isConfigured || (!loading && !user);

  if (failed) {
    const message = urlError
      ? urlError
      : !isConfigured
      ? 'Auth is not configured yet. Fill in frontend/.env and restart the dev server.'
      : "We couldn't finish signing you in. The link may have expired — please try again.";

    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#111827', margin: '0 0 10px' }}>
            Sign-in didn't complete
          </h1>
          <p style={{ color: '#6b7280', fontSize: '15px', lineHeight: 1.5, margin: '0 0 22px' }}>
            {message}
          </p>
          <Link
            to="/login"
            style={{
              display: 'inline-block',
              background: '#1d4ed8',
              color: '#ffffff',
              textDecoration: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              fontWeight: 700,
              fontSize: '15px',
            }}
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#111827', margin: '0 0 8px' }}>
          Signing you in…
        </h1>
        <p style={{ color: '#6b7280', fontSize: '15px', margin: 0 }}>
          One moment while we finish connecting your account.
        </p>
      </div>
    </div>
  );
}
